/**
 * One row, every column of it, whole — and the place its data is changed.
 *
 * Three modes on the same panel:
 *
 *   view  every value in full, the grid's clipped cells readable
 *   edit  every value an input; Save writes an UPDATE of only what changed,
 *         keyed on the primary key
 *   new   every input empty; a blank field takes the column's default, and
 *         Save writes the INSERT
 *
 * NULL is its own state, never an empty box: an empty string and a missing
 * value are different things in a database, and a form that cannot tell them
 * apart silently writes the wrong one.
 *
 * It runs the statement it builds through the same path as the SQL console —
 * the app's own role, one statement — and prints that statement before Save,
 * because it is also what belongs in a script or a migration.
 *
 * A view, or a table without a primary key, has no row an UPDATE or DELETE
 * can name safely: the panel stays read-only for those.
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDatabaseColumn } from '@back4app2/sdk';
import back4app2 from '../../back4app2';
import { DsButton } from '../../components/ds';
import { CopyIcon } from '../../components/ds/icons';
import { DrawerPanel } from './SideDrawer';
import {
  CellValue,
  formatCell,
  isBooleanType,
  isTemporalType,
  quoteIdent,
  quotedTarget,
  shortTypeName,
  sqlLiteral,
} from './cells';

type Mode = 'view' | 'edit' | 'new';

interface FieldState {
  text: string;
  isNull: boolean;
}

const initialFields = (columns: AppDatabaseColumn[], row?: unknown[]) =>
  Object.fromEntries(columns.map((column, index) => {
    const value = row ? row[index] : undefined;

    return [column.name, {
      text: row && value !== null && value !== undefined ? formatCell(value) : '',
      isNull: !!row && (value === null || value === undefined),
    }];
  })) as Record<string, FieldState>;

const RowInspector = ({ appId, schema, table, isEditable, columns, row, onClose, onSaved, onDeleted }: {
  appId: string,
  schema: string,
  table: string,
  /** A table, not a view. */
  isEditable: boolean,
  columns: AppDatabaseColumn[],
  /** The row open, or undefined to create one. */
  row?: unknown[],
  onClose: () => void,
  /**
   * The statement ran. For an edit, the row as it now reads, so the panel can
   * keep showing it while the page reloads; nothing for an insert.
   */
  onSaved: (row?: unknown[]) => void,
  onDeleted: () => void,
}) => {
  const [mode, setMode] = useState<Mode>(row ? 'view' : 'new');
  const [fields, setFields] = useState<Record<string, FieldState>>(() => initialFields(columns, row));
  const [isRunning, setIsRunning] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  /**
   * The statement, read on purpose rather than in the way.
   *
   * It used to sit open above the buttons, and on a drawer that is mostly
   * fields it was a block of SQL taking a third of the panel to say what the
   * two buttons under it already say. Closed it stays one click from being
   * read — which is the promise this screen makes about every write — and the
   * fields keep the room.
   */
  const [isShowingStatement, setIsShowingStatement] = useState(false);

  // A different row, or a switch to "new", starts over.
  useEffect(() => {
    setMode(row ? 'view' : 'new');
    setFields(initialFields(columns, row));
    setFailure(undefined);
    setIsConfirmingDelete(false);
    // Each row starts with the panel showing the row, not SQL. Within one row
    // the choice sticks: someone reading every statement should not have to
    // ask twice.
    setIsShowingStatement(false);
  }, [row, columns]);

  const target = quotedTarget(schema, table);
  const keys = columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.isPrimaryKey);
  const canWrite = isEditable && keys.length > 0;

  const whereClause = row
    ? keys.map(({ column, index }) => `${quoteIdent(column.name)} = ${sqlLiteral(row[index])}`).join(' AND ')
    : '';

  const title = row
    ? (keys.length ? keys : [{ column: columns[0], index: 0 }])
      .filter(({ column }) => column)
      .map(({ column, index }) => `${column.name} = ${formatCell(row[index])}`)
      .join(', ')
    : `New row in ${table}`;

  const literalFor = (field: FieldState) => field.isNull ? 'NULL' : sqlLiteral(field.text);

  const statement = useMemo(() => {
    if (mode === 'edit' && row) {
      const initial = initialFields(columns, row);
      const changed = columns.filter(column => {
        const now = fields[column.name];
        const before = initial[column.name];

        return now.isNull !== before.isNull || (!now.isNull && now.text !== before.text);
      });

      if (changed.length === 0) {
        return '';
      }

      const width = Math.max(...changed.map(column => quoteIdent(column.name).length));

      return [
        `UPDATE ${target}`,
        `   SET ${changed
          .map(column => `${quoteIdent(column.name).padEnd(width)} = ${literalFor(fields[column.name])}`)
          .join(',\n       ')}`,
        ` WHERE ${whereClause};`,
      ].join('\n');
    }

    if (mode === 'new') {
      // A blank field is left out of the INSERT, so the column's own default
      // — a sequence, now(), or NULL — decides it.
      const given = columns.filter(column => {
        const field = fields[column.name];

        return field.isNull || field.text !== '';
      });

      if (given.length === 0) {
        return `INSERT INTO ${target} DEFAULT VALUES;`;
      }

      return [
        `INSERT INTO ${target}`,
        `  (${given.map(column => quoteIdent(column.name)).join(', ')})`,
        `VALUES`,
        `  (${given.map(column => literalFor(fields[column.name])).join(', ')});`,
      ].join('\n');
    }

    return '';
  }, [mode, row, columns, fields, target, whereClause]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = (sql: string, after: () => void) => {
    setIsRunning(true);
    setFailure(undefined);

    back4app2
      .runAppDatabaseStatement(appId, sql)
      .then(outcome => {
        if (outcome.errorMessage) {
          setFailure(outcome.errorMessage);
          return;
        }
        after();
      })
      .catch((e: Error) => setFailure(e.message))
      .finally(() => setIsRunning(false));
  };

  const save = () => {
    if (!statement) {
      return;
    }

    run(statement, () => {
      if (mode === 'new') {
        onSaved();
        return;
      }

      const next = columns.map((column, index) => {
        const field = fields[column.name];
        const before = row ? row[index] : undefined;

        if (field.isNull) {
          return null;
        }

        // Unchanged values keep their original shape (a number, a JSON
        // object); edited ones are shown as typed until the page reloads.
        return before !== null && before !== undefined && formatCell(before) === field.text
          ? before
          : field.text;
      });

      setMode('view');
      onSaved(next);
    });
  };

  const deleteSql = row ? `DELETE FROM ${target}\n WHERE ${whereClause};` : '';

  const setField = (name: string, patch: Partial<FieldState>) =>
    setFields(current => ({ ...current, [name]: { ...current[name], ...patch } }));

  const isEditing = mode !== 'view';
  // 44px targets on a phone, the desktop 38px above it.
  const buttonSize = typeof window !== 'undefined' && window.innerWidth < 768 ? 'touch' : 'lg';

  const footer = (<>
      {canWrite ? (
        <div className="flex flex-col gap-2.5 w-full">
          {isEditing ? <>
            {statement ? (
              <StatementDisclosure
                sql={statement}
                isOpen={isShowingStatement}
                onToggle={() => setIsShowingStatement(current => !current)}
              />
            ) : (
              <p className="m-0 text-ds-125 text-navy-300">Change a value to see the statement.</p>
            )}
            {failure ? (
              <p className="m-0 font-courrier text-ds-115 leading-[1.5] text-state-err">{failure}</p>
            ) : null}
            <div className="flex gap-2">
              <DsButton
                variant="ghost"
                size={buttonSize}
                isDisabled={isRunning}
                onClick={() => {
                  if (mode === 'new') {
                    onClose();
                    return;
                  }
                  setMode('view');
                  setFields(initialFields(columns, row));
                  setFailure(undefined);
                }}
              >
                Cancel
              </DsButton>
              <DsButton
                variant="primary"
                size={buttonSize}
                className="flex-1"
                isDisabled={isRunning || !statement}
                onClick={save}
              >
                {isRunning ? 'Saving…' : mode === 'new' ? 'Insert row' : 'Save changes'}
              </DsButton>
            </div>
          </> : isConfirmingDelete ? <>
            <p className="m-0 text-ds-135 text-navy-100">Delete this row? This cannot be undone.</p>
            <StatementDisclosure
              sql={deleteSql}
              isOpen={isShowingStatement}
              onToggle={() => setIsShowingStatement(current => !current)}
            />
            {failure ? (
              <p className="m-0 font-courrier text-ds-115 leading-[1.5] text-state-err">{failure}</p>
            ) : null}
            <div className="flex gap-2">
              <DsButton
                variant="ghost"
                size={buttonSize}
                className="flex-1"
                isDisabled={isRunning}
                onClick={() => { setIsConfirmingDelete(false); setFailure(undefined); }}
              >
                Keep row
              </DsButton>
              <DsButton
                variant="danger"
                size={buttonSize}
                className="flex-1"
                isDisabled={isRunning}
                onClick={() => run(deleteSql, onDeleted)}
              >
                {isRunning ? 'Deleting…' : 'Delete'}
              </DsButton>
            </div>
          </> : (
            <div className="flex gap-2">
              <DsButton variant="subtle" size={buttonSize} className="flex-1" onClick={() => setMode('edit')}>
                Edit row
              </DsButton>
              <DsButton variant="danger" size={buttonSize} onClick={() => setIsConfirmingDelete(true)}>
                Delete
              </DsButton>
            </div>
          )}
        </div>
      ) : isEditable && !keys.length ? (
        <p className="m-0 w-full text-ds-115 text-navy-300">
          This table has no primary key, so its rows can't be edited from here. Use the SQL console.
        </p>
      ) : null}
  </>);

  return (
    <DrawerPanel
      eyebrow={row ? 'row' : 'new row'}
      title={<span className="font-courrier text-[17px]" title={title}>{title}</span>}
      onClose={onClose}
      footer={canWrite || (isEditable && !keys.length) ? footer : undefined}
    >
      <div className="-mt-3">
        {columns.map((column, index) => {
          const value = row ? row[index] : undefined;
          const field = fields[column.name];
          const type = shortTypeName(column.dataType);

          return (
            <div key={column.name} className="group flex flex-col gap-1.5 py-3 border-b border-navy-700 last:border-b-0">
              <div className="flex items-baseline gap-2">
                {/* The name is what you are looking for; the type and the
                    constraints are what you check once. They used to be one
                    dim mono line where the three read as equals. */}
                <span className="min-w-0 truncate font-courrier text-ds-125 text-navy-100">
                  {column.name}
                </span>
                <span className="flex-none font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300">
                  {type}
                </span>
                {column.isPrimaryKey ? (
                  <span className="flex-none h-[17px] px-1.5 rounded-[4px] border border-action/50 bg-action/10 font-courrier text-ds-105 uppercase tracking-[0.06em] text-action-soft">
                    pk
                  </span>
                ) : !column.isNullable ? (
                  <span className="flex-none font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-400">
                    not null
                  </span>
                ) : null}

                {/* One value is the thing people take out of this panel — a
                    key to paste into a query, an e-mail to search for. Shown
                    on hover and on keyboard focus, never as a row of icons. */}
                {!isEditing && value !== null && value !== undefined ? (
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(formatCell(value))}
                    title={`Copy ${column.name}`}
                    aria-label={`Copy ${column.name}`}
                    className="b4-focus ml-auto flex-none h-5 px-1.5 rounded-[4px] font-courrier text-ds-105 uppercase tracking-[0.06em] text-navy-400 opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100 hover:text-white hover:bg-navy-600 transition-opacity duration-[120ms]"
                  >
                    copy
                  </button>
                ) : null}
                {isEditing && column.isNullable && field ? (
                  <button
                    type="button"
                    onClick={() => setField(column.name, { isNull: !field.isNull })}
                    aria-pressed={field.isNull}
                    title={field.isNull ? 'Set a value instead' : 'Set to NULL'}
                    className={[
                      'b4-focus ml-auto flex-none h-5 px-1.5 rounded-[4px] border font-courrier text-[length:calc(10.5px*var(--b4-type,1))] tracking-[0.06em] transition-colors duration-[120ms]',
                      field.isNull
                        ? 'border-action bg-action/15 text-action-soft'
                        : 'border-navy-500 text-navy-300 hover:text-white',
                    ].join(' ')}
                  >
                    NULL
                  </button>
                ) : null}
              </div>

              {!isEditing ? (
                value === null || value === undefined ? (
                  <span className="font-courrier text-[length:calc(13px*var(--b4-type,1))] italic text-navy-300">null</span>
                ) : isTemporalType(column.dataType) || isBooleanType(column.dataType) ? (
                  // Drawn the way the grid draws it, so the two agree: a
                  // timestamp read here and the same one read in the table
                  // were printing as different things.
                  <span
                    className="font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100"
                    title={formatCell(value)}
                  >
                    <CellValue value={value} dataType={column.dataType} />
                  </span>
                ) : (
                  // Capped, and scrolls: one 40kB json column used to push
                  // every column under it off the panel.
                  <span className="block max-h-[200px] overflow-auto b4-thin font-courrier text-[length:calc(13px*var(--b4-type,1))] text-navy-100 break-all whitespace-pre-wrap">
                    {formatCell(value)}
                  </span>
                )
              ) : field.isNull ? (
                <span className="h-9 flex items-center px-3 rounded-control border border-dashed border-navy-500 font-courrier text-[length:calc(13px*var(--b4-type,1))] italic text-navy-300">
                  null
                </span>
              ) : (
                <FieldInput
                  column={column}
                  text={field.text}
                  isNew={mode === 'new'}
                  onChange={text => setField(column.name, { text })}
                />
              )}
            </div>
          );
        })}
      </div>
    </DrawerPanel>
  );
};

/**
 * The box for one value, shaped by its type.
 *
 * A json column gets the box AND a way to lay it out: `{"plan":"free",...}`
 * arrives from Postgres on one line, which is how it is stored and the worst
 * way there is to read or edit it. Beautify is local and reversible — it
 * reformats the text in the box, it does not write anything.
 */
const FieldInput = ({ column, text, isNew, onChange }: {
  column: AppDatabaseColumn,
  text: string,
  isNew: boolean,
  onChange: (text: string) => void,
}) => {
  const [complaint, setComplaint] = useState<string | undefined>();
  const base = 'b4-focus w-full rounded-control border border-navy-400 bg-navy-900 px-3 font-courrier text-[length:calc(13px*var(--b4-type,1))] text-white placeholder:text-navy-300 focus:border-action outline-none';

  // In a new row the placeholder says what a blank box means for this column.
  const placeholder = isNew
    ? column.defaultValue
      ? `default: ${column.defaultValue}`
      : column.isNullable ? 'blank = null' : 'required'
    : '';

  if (isBooleanType(column.dataType)) {
    return (
      <select value={text} onChange={event => onChange(event.target.value)} className={`${base} h-9`}>
        {isNew ? <option value="">{placeholder}</option> : null}
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  const isJson = /^json/.test(shortTypeName(column.dataType));
  const isLong = isJson || text.length > 48 || text.includes('\n');

  const lay = (space: number | undefined) => {
    try {
      onChange(JSON.stringify(JSON.parse(text), null, space));
      setComplaint(undefined);
    } catch {
      // Said here rather than by Postgres: the statement has not been written
      // yet, and "invalid input syntax for type json" at save time is a slower
      // way to learn the same thing.
      setComplaint("That isn't valid JSON, so there is nothing to lay out.");
    }
  };

  return isLong ? (
    <div className="flex flex-col gap-1.5">
      {isJson ? (
        <div className="flex items-center gap-2">
          <JsonAction onClick={() => lay(2)}>beautify</JsonAction>
          <JsonAction onClick={() => lay(undefined)}>minify</JsonAction>
        </div>
      ) : null}
      <div className="relative">
        <textarea
          value={text}
          onChange={event => { onChange(event.target.value); setComplaint(undefined); }}
          placeholder={placeholder}
          // A laid-out object needs the room it just took: 3 rows of a 14-line
          // value is a scrollbar where a shape should be.
          rows={Math.min(isJson ? 16 : 8, Math.max(isJson ? 4 : 3, text.split('\n').length))}
          spellCheck={false}
          className={`${base} block py-2 pr-11 leading-[1.5] resize-y`}
        />
        {text ? <CopyButton text={text} label={`Copy ${column.name}`} /> : null}
      </div>
      {complaint ? (
        <span className="font-courrier text-ds-115 leading-[1.5] text-state-err">{complaint}</span>
      ) : null}
    </div>
  ) : (
    <input
      value={text}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
      spellCheck={false}
      className={`${base} h-9`}
    />
  );
};

/**
 * "show statement" — the SQL, on request.
 *
 * Nothing about what runs changes: the same text, built the same way, and it
 * is still the thing that runs. Only whether it is occupying the panel before
 * anyone has asked to read it.
 */
const StatementDisclosure = ({ sql, isOpen, onToggle }: {
  sql: string,
  isOpen: boolean,
  onToggle: () => void,
}) => (
  <div className="flex flex-col gap-2 w-full">
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="b4-focus flex-none h-6 px-2 rounded-[4px] border border-navy-500 font-courrier text-ds-105 uppercase tracking-[0.06em] text-navy-300 hover:text-white hover:border-navy-300 transition-colors duration-[120ms]"
      >
        {isOpen ? 'hide statement' : 'show statement'}
      </button>
    </div>
    {isOpen ? (
      <div className="relative">
        <pre className="m-0 max-h-[140px] overflow-auto b4-thin p-2.5 pr-11 rounded-control border border-navy-500 bg-navy-950 font-courrier text-ds-115 leading-[1.6] text-navy-200 whitespace-pre-wrap">
          {sql}
        </pre>
        <CopyButton text={sql} label="Copy the statement" />
      </div>
    ) : null}
  </div>
);

/**
 * Copy, inside the box it copies.
 *
 * Top right of the text rather than a word beside it: the box is what is being
 * taken, the corner is where every editor puts this, and a labelled button
 * outside read as one more control to consider before saving.
 */
const CopyButton = ({ text, label }: { text: string, label: string }) => (
  <button
    type="button"
    onClick={() => navigator.clipboard?.writeText(text)}
    title={label}
    aria-label={label}
    className="b4-focus absolute top-1.5 right-1.5 flex items-center justify-center w-7 h-7 rounded-[5px] border border-navy-500 bg-navy-800/90 text-navy-300 hover:text-white hover:border-navy-300 transition-colors duration-[120ms]"
  >
    <CopyIcon size={13} />
  </button>
);

/** The quiet mono action above a json box. */
const JsonAction = ({ onClick, children }: {
  onClick: () => void,
  children: React.ReactNode,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="b4-focus flex-none h-5 px-1.5 rounded-[4px] border border-navy-500 font-courrier text-ds-105 uppercase tracking-[0.06em] text-navy-300 hover:text-white hover:border-navy-300 transition-colors duration-[120ms]"
  >
    {children}
  </button>
);

export default RowInspector;
